import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ItemNftMintStatus,
  NftTokenDetailFragmentDoc,
  NftTokenQuery,
  NftTokenQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getAccessToken } from '@/core/auth'
import { useMetamask } from '@/core/web3/metamask'
import { useWeb3authWalletConnect } from '@/core/web3/web3auth/hooks'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('nftItemDetail')
  const { router, routerPages, generatePath } = useRouter<{
    itemId: string
    serialId: string
  }>()
  const { showMessage, showError } = useMessage()
  const { userAuthorizeWalletBySignatureMessage } = useMetamask()

  const { itemId, serialId } = router.query

  const { data, loading, refetch } = useQuery<
    NftTokenQuery,
    NftTokenQueryVariables
  >(NFT_TOKEN, {
    variables: { id: itemId },
  })

  const serial = data?.portfolio?.serials?.find(
    serial => serial.serialId === serialId
  )

  const { web3authWalletLoading } = useWeb3authWalletConnect({
    loading: loading,
    walletAddress: data?.user?.cryptoWallet?.address,
    isArtist: true,
    refetch,
  })

  const [mintItemNft, { loading: issuing }] = useMutation(MINT_ITEM_NFT)

  return {
    state: {
      loading: loading || web3authWalletLoading,
      issuing,
      item: {
        id: data?.portfolio?.item.id ?? null,
        title: data?.portfolio?.item.title ?? null,
        royalty: data?.portfolio?.item?.nft?.chains?.[0]?.royalty ?? null,
        serialNumber: serial?.formattedSerialNumber ?? null,
        tokenId: serial?.tokenId ?? null,
        chainName: data?.portfolio?.item?.nft?.chains?.[0]?.chainName ?? null,
      },
      nftToken: serial?.nftToken ?? null,
      isConnect: !!data?.user?.cryptoWallet?.address,
      nftActive: serial?.nftToken?.status === ItemNftMintStatus.Completed,
    },
    actions: {
      onIssueNft: async () => {
        await mintItemNft({ variables: { inputs: { itemId, serialId } } })
          .then(res => {
            showMessage(t('issued'))
            router.push(generatePath(routerPages.OwnedItemDetail, { itemId }))
          })
          .catch(e => showError(e))
      },
      connectMetamask: async () => {
        await userAuthorizeWalletBySignatureMessage()
        await refetch()

        return
      },
      connectToWeb3auth: async () => {
        /**
         * web3auth <=> firebase auth連携用のidToken取得
         */
        const idToken = await getAccessToken({ forceUpdateToken: true })
        if (!idToken) throw new Error('get jwtToke failed')

        /**
         * web3auth <=> firebase auth連携
         */
        await import('@/core/web3/web3auth').then(m =>
          m.connectToWeb3Auth(idToken)
        )

        return
      },
    },
  }
})

const NFT_TOKEN = gql`
  query NftToken($id: ID!) {
    user {
      id
      cryptoWallet {
        address
      }
    }
    portfolio(id: $id) {
      id
      item {
        id
        title
        nft {
          chains {
            chainName
            royalty
          }
        }
      }
      serials {
        serialId
        formattedSerialNumber
        tokenId
        nftToken {
          ...NftTokenDetail
        }
      }
    }
  }
  ${NftTokenDetailFragmentDoc}
`

const MINT_ITEM_NFT = gql`
  mutation MintItemNft($inputs: MintItemNftInput!) {
    mintItemNft(inputs: $inputs) {
      id
      chain
      createdAt
      formattedCreatedAt @client
      ownedByUserAddress
      metadata {
        item {
          item {
            id
            title
            issuedArtist {
              id
              displayName
              avatar {
                uri
              }
            }
            nft {
              chains {
                chainName
              }
            }
          }
          itemSerial {
            id
            formattedSerialNumber
          }
        }
      }
      tokenId
      tokenURI
      status
    }
  }
`
